import { memo, useCallback } from 'react';

import { faMicrophone, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  DatePicker,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@repo/videa-components';
import { isRecordingSessionActiveAtom } from '@repo/videa-notes';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { shallowEqual, useSelector } from 'react-redux';

import { useGetDateFNSLocale } from '@/locales/helpers';
import { VoiceNotesSource } from '@/modules/videaNotes/types';
import { usePreferences } from '@/react-query/usePreferences';
import {
  mixpanelEvents,
  useTrackEventWithOptionalParams,
} from '@/utils/mixpanel';

import {
  ambientViewTypeAtom,
  operatoryFilterAtom,
  recentNotesViewAtom,
  selectDateAtom,
  selectedProvidersAtom,
  templatesSearchAtom,
} from '../../../../modules/patientList/atoms';
import { PatientListPageLayoutContextMap } from '../../../../modules/patientList/types';
import {
  VideaNotesUIState,
  useOpenVideaNotes,
} from '../../../../modules/videaNotes/helpers';
import { PatientSearch } from '../PatientSearch';

/**
 * Memoized Start Note button - isolates isRecordingSessionActive subscription
 * so recording state changes don't re-render the entire topbar
 */
const StartNoteButton = memo(
  ({ date, user }: { date: Date | null; user: any }) => {
    const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
    const { data: prefs } = usePreferences();
    const openVideaNotes = useOpenVideaNotes();

    const handleStartNote = useCallback(() => {
      // Create a minimal appointment data for quick note (no existing appointment)
      const mockAppointment = {
        appointment: {
          unifiedId: '', // Empty string for new notes
          start: (date || new Date()).toISOString(),
          estimatedVisitId: null, // No existing visit
        },
        patient: {
          firstName: 'Quick',
          lastName: 'Note',
          displayName: 'Quick Note',
        },
        lastTreatments: {
          lastBw: null,
          lastFluoride: null,
          lastFmx: null,
          lastPerioChart: null,
          lastPerioMaintenance: null,
          lastProphy: null,
          lastSrp: null,
        },
      };

      openVideaNotes(
        user,
        mockAppointment,
        VideaNotesUIState.PATIENT_INFO,
        VoiceNotesSource.QUICK_NOTE,
      );
    }, [date, user, openVideaNotes]);

    if (!prefs?.enableQuickNotes) {
      return null;
    }

    return (
      <Button
        onClick={handleStartNote}
        className="vai-bg-brand-primary hover:vai-bg-brand-primary/90 vai-text-white hover:vai-text-white vai-border-none vai-flex vai-items-center vai-gap-2"
        disabled={isRecordingSessionActive}
      >
        <FontAwesomeIcon icon={faMicrophone} className="vai-h-4 vai-w-4" />
        <Trans>Start Note</Trans>
      </Button>
    );
  },
);

StartNoteButton.displayName = 'StartNoteButton';

export const AmbientTopbarContent = memo(() => {
  // hooks
  const { t } = useLingui();
  const dateLocale = useGetDateFNSLocale();
  // Memoized selector to prevent re-renders on unrelated Redux state changes
  const user = useSelector(
    (state: any) => state?.login?.user,
    shallowEqual,
  );
  const { trackEvent } = useTrackEventWithOptionalParams();

  // atoms
  const [date, setDate] = useAtom(selectDateAtom);
  const [templatesSearch, setTemplatesSearch] = useAtom(templatesSearchAtom);
  const [isRecentNotesView, setIsRecentNotesView] = useAtom(
    recentNotesViewAtom,
  );
  const ambientViewType = useAtomValue(ambientViewTypeAtom);
  const setSelectedOperatories = useSetAtom(operatoryFilterAtom);
  const setSelectedProviders = useSetAtom(selectedProvidersAtom);

  const isTemplatesView = ambientViewType === 'templates';

  const handleSelectDate = useCallback(
    (newDate: Date) => {
      setDate(newDate);
      setSelectedOperatories([]);
      setSelectedProviders([]);
    },
    [setDate, setSelectedOperatories, setSelectedProviders],
  );

  const handleViewChange = useCallback(
    (value: string) => {
      const newView = value === 'recent' ? 'recent' : 'schedule';

      // Track the view toggle
      trackEvent(mixpanelEvents.videaNotesViewToggle, {
        view: newView,
        previousView: isRecentNotesView ? 'recent' : 'schedule',
      });

      if (value === 'recent') {
        setIsRecentNotesView(true);
      } else {
        setIsRecentNotesView(false);
        if (!date) {
          setDate(new Date());
        }
      }
    },
    [trackEvent, isRecentNotesView, setIsRecentNotesView, date, setDate],
  );

  return (
    <div className="vai-flex vai-items-center vai-justify-between vai-w-full">
      <div className="vai-flex vai-items-center vai-gap-4">
        {isTemplatesView ? (
          // Templates view - only search bar
          <div className="vai-w-80 vai-relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-h-4 vai-w-4"
            />
            <Input
              type="text"
              placeholder={t`Search templates`}
              value={templatesSearch}
              onChange={(e) => setTemplatesSearch(e.target.value)}
              className="vai-pl-10"
            />
          </div>
        ) : (
          // Notes view - side-by-side toggle, date picker (conditional), and patient search
          <>
            {/* View Type Toggle */}
            <Tabs
              value={isRecentNotesView ? 'recent' : 'schedule'}
              onValueChange={handleViewChange}
            >
              <TabsList className="vai-h-10 vai-bg-gray-100">
                <TabsTrigger value="schedule" className="vai-px-4">
                  <Trans>Patient Schedule</Trans>
                </TabsTrigger>
                <TabsTrigger value="recent" className="vai-px-4">
                  <Trans>Recent Notes</Trans>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Date Picker - only show when in schedule mode */}
            {!isRecentNotesView && (
              <DatePicker
                date={date || undefined}
                setDate={handleSelectDate}
                placeholderText={t`Pick a date`}
                locale={dateLocale}
                showPreviousNextButtons
                showTodayButton
                todayButtonText={t`Back to Today`}
              />
            )}

            {/* Patient Search - always visible */}
            <div className="vai-w-80">
              <PatientSearch
                context={PatientListPageLayoutContextMap.Ambient}
              />
            </div>
          </>
        )}
      </div>

      {/* Start Note Button - Right justified, only for Notes view */}
      {!isTemplatesView && <StartNoteButton date={date} user={user} />}
    </div>
  );
});

AmbientTopbarContent.displayName = 'AmbientTopbarContent';
